import { Paper, Stack, Typography } from '@mui/material';
import { Demographics } from 'features/lead/lead-questionnaire/lead-questionnaire.types';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiRadioItem from 'features/ui/base-components/pai-radio-item';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  demographicAnswers: Demographics;

  handleBranchCheckboxChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleCompanySizeRadioChange(value: string): () => void;

  handleTeamSizeRadioChange(value: string): () => void;

  handleManagementFieldRadioChange(value: string): () => void;

  handleTeamTypeRadioChange(value: string): () => void;

  handleCustomerResponsibilityRadioChange(value: string): () => void;

  handleResponsibilityFieldRadioChange(value: string): () => void;
}

const DemographicQuestionsContainer: React.FC<Props> = ({
  demographicAnswers,
  handleBranchCheckboxChange,
  handleCompanySizeRadioChange,
  handleTeamSizeRadioChange,
  handleManagementFieldRadioChange,
  handleTeamTypeRadioChange,
  handleCustomerResponsibilityRadioChange,
  handleResponsibilityFieldRadioChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const branchOptions = [
    { label: 'agriculture', value: 'Agrarwirtschaft' },
    { label: 'construction', value: 'Baugewerbe' },
    { label: 'chemistryRawMaterials', value: 'Chemie & Rohstoffe' },
    { label: 'industryCraftsmanship', value: 'Industrie & Handwerk' },
    { label: 'consulting', value: 'Beratung' },
    { label: 'eCommerceMailOrder', value: 'E-Commerce & Versandhandel' },
    { label: 'energyEnvironment', value: 'Energie & Umwelt' },
    { label: 'financeInsuranceRealEstate', value: 'Finanzen, Versicherungen & Immobilien' },
    { label: 'societyEducationSocialJustice', value: 'Gesellschaft (Bildung, Soziales, Justiz)' },
    { label: 'trade', value: 'Handel' },
    { label: 'consumption', value: 'Konsum' },
    { label: 'mediaMarketing', value: 'Medien & Marketing' },
    { label: 'pharmaHealth', value: 'Pharma & Gesundheit' },
    { label: 'metalElectronics', value: 'Metall & Elektronik' },
    { label: 'technologyTelecommunication', value: 'Technik & Telekommunikation' },
    { label: 'tourismHospitality', value: 'Tourismus & Gastronomie' },
    { label: 'sportsWellness', value: 'Sport & Wellness' },
    { label: 'transportLogistics', value: 'Verkehr & Logistik' },
    { label: 'administrationPublicService', value: 'Verwaltung & öffentlicher Dienst' },
    { label: 'scienceResearch', value: 'Wissenschaft & Forschung' },
  ];

  const companySizeOptions = [
    { label: 'upTo100Employees', value: 'bis 100 Mitarbeitende' },
    { label: 'to499Employees', value: '101 bis 499 Mitarbeitende' },
    { label: 'to4999Employees', value: '500 bis 4.999 Mitarbeitende' },
    { label: 'to19999Employees', value: '5.000 bis 19.999 Mitarbeitende' },
    { label: 'to50000Employees', value: '20.000 bis 50.000 Mitarbeitende' },
    { label: 'moreThan50000Employees', value: 'mehr als 50.000 Mitarbeitende' },
  ];

  const teamSizeOptions = ['1-5', '6-10', '11-20', '21-50', '51-100', '> 100'];

  const managementFieldOptions = [
    { label: 'executive', value: 'Executive' },
    { label: 'departmentManagement', value: 'Bereichsleitung' },
    { label: 'divisionalManagement', value: 'Abteilungsleitung' },
    { label: 'leadQuestionnaireTeamManagement', value: 'Teamleitung' },
    { label: 'noManagementFunction', value: 'keine Managementfunktion' },
  ];

  const teamTypeOptions = [
    { label: 'specialistTeam', value: 'Spezialistenteam' },
    { label: 'scienceTeam', value: 'Wissenschaftsteam' },
    { label: 'interprofessionalTeam', value: 'Interprofessionelles Team' },
    { label: 'salesTeam', value: 'Vertriebsteam' },
    { label: 'operationalTeam', value: 'Operatives Team' },
    { label: 'other', value: 'Andere' },
  ];

  const customerResponsibilityOptions = [
    { label: 'yes', value: 'Ja' },
    { label: 'no', value: 'Nein' },
  ];

  const responsibilityFieldOptions = [
    { label: 'financeAccounting', value: 'Finanzen/Rechnungswesen' },
    { label: 'controlling', value: 'Controlling' },
    { label: 'humanResources', value: 'Personal' },
    { label: 'backOffice', value: 'Back Office' },
    { label: 'business', value: 'Business' },
    { label: 'developmentStrategy', value: 'Development/Strategie' },
    { label: 'riskManagement', value: 'Risikomanagement' },
    { label: 'informatics', value: 'IT' },
    { label: 'communicationMarketing', value: 'Kommunikation/Marketing' },
    { label: 'serviceCenter', value: 'Service Center' },
    { label: 'salesSupport', value: 'Vertriebsunterstützung' },
    { label: 'other', value: 'Andere' },
  ];

  return (
    <Stack gap={4}>
      <Typography className={classes.title}>{t('leadQuestionnaireDemographicQuestions')}</Typography>
      <Stack gap={2}>
        {/*Branch*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsBranch')}</Typography>
          </div>
          <Stack className={classes.content} direction={'row'} gap={2} flexWrap={'wrap'}>
            {branchOptions.map((item) => (
              <PaiCheckbox
                key={item.value}
                checked={item.value === demographicAnswers.branch}
                label={t(item.label)}
                value={item.value}
                onChange={handleBranchCheckboxChange}
              />
            ))}
          </Stack>
        </Paper>
        {/*Company size*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsCompanySize')}</Typography>
          </div>
          <Stack className={classes.content} gap={1}>
            {companySizeOptions.map((item) => (
              <PaiRadioItem
                key={item.value}
                value={item.value}
                selectedValue={demographicAnswers.companySize}
                label={t(item.label)}
                handleSelectItem={handleCompanySizeRadioChange}
              />
            ))}
          </Stack>
        </Paper>
        {/*Team size*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsTeamSize')}</Typography>
          </div>
          <Stack className={classes.content} gap={1}>
            {teamSizeOptions.map((item) => (
              <PaiRadioItem
                key={item}
                value={item}
                selectedValue={demographicAnswers.teamSize}
                label={item}
                handleSelectItem={handleTeamSizeRadioChange}
              />
            ))}
          </Stack>
        </Paper>
        {/*Management field*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsManagementField')}</Typography>
          </div>
          <Stack className={classes.content} gap={1}>
            {managementFieldOptions.map((item) => (
              <PaiRadioItem
                key={item.value}
                value={item.value}
                selectedValue={demographicAnswers.managementField}
                label={t(item.label)}
                handleSelectItem={handleManagementFieldRadioChange}
              />
            ))}
          </Stack>
        </Paper>
        {/*Team type*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsTeamType')}</Typography>
          </div>
          <Stack className={classes.content} gap={1}>
            {teamTypeOptions.map((item) => (
              <PaiRadioItem
                key={item.value}
                value={item.value}
                selectedValue={demographicAnswers.teamType}
                label={t(item.label)}
                handleSelectItem={handleTeamTypeRadioChange}
              />
            ))}
          </Stack>
        </Paper>
        {/*Customer responsibility*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsCustomerResponsibility')}</Typography>
          </div>
          <Stack className={classes.content} gap={1}>
            {customerResponsibilityOptions.map((item) => (
              <PaiRadioItem
                key={item.value}
                value={item.value}
                selectedValue={demographicAnswers.customerResponsibility}
                label={t(item.label)}
                handleSelectItem={handleCustomerResponsibilityRadioChange}
              />
            ))}
          </Stack>
        </Paper>
        {/*Responsibility field*/}
        <Paper className={classes.textContainer} elevation={2} square={true}>
          <div className={classes.header}>
            <Typography className={classes.categoryName}>{t('leadQuestionnaireDemographicQuestionsResponsibilityField')}</Typography>
          </div>
          <Stack className={classes.content} gap={1}>
            {responsibilityFieldOptions.map((item) => (
              <PaiRadioItem
                key={item.value}
                value={item.value}
                selectedValue={demographicAnswers.responsibilityField}
                label={t(item.label)}
                handleSelectItem={handleResponsibilityFieldRadioChange}
              />
            ))}
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: theme.typography.pxToRem(32),
    textAlign: 'center',
    fontWeight: 'bold',
  },
  textContainer: {
    background: 'rgba(0,0,0,0.03)',
  },
  content: {
    padding: '16px',
  },
  header: {
    backgroundColor: theme.palette.primary.main,
    color: '#FFF',
    padding: '8px 48px 8px 16px',
    minHeight: '60px',
    display: 'flex',
    alignItems: 'center',
  },
  categoryName: {
    fontSize: theme.typography.pxToRem(20),
  },
}));

export default DemographicQuestionsContainer;
